/*
 * FreshTrends wishlist.
 *
 * Phase 1: the list lives on the shopper's device (localStorage). Phase 2 will sync it to the
 * customer account; everything that changes the list goes through window.FTWishlist and fires
 * `ft:wishlist:change`, so the sync can hook in without touching the buttons or the page.
 *
 * Markup contract:
 *   [data-ft-wl]            heart button (snippets/ft-wishlist-heart.liquid), carries data-handle
 *   [data-ft-wl-count]      header bubble that shows the number of saved items
 *   [data-ft-wl-page]       the wishlist page (sections/ft-wishlist.liquid)
 */
(function () {
  'use strict';
  if (window.FTWishlist) return;

  var KEY = 'ft_wishlist_v1';
  var MAX_ITEMS = 100;
  var memory = null; // fallback when storage is blocked (private windows, some in-app browsers)

  function load() {
    try {
      var raw = JSON.parse(localStorage.getItem(KEY) || '[]');
      if (Array.isArray(raw)) {
        return raw.filter(function (x) { return x && typeof x.h === 'string' && x.h; });
      }
    } catch (e) { /* fall through */ }
    return memory || [];
  }

  var items = load();

  function save() {
    memory = items;
    try { localStorage.setItem(KEY, JSON.stringify(items)); } catch (e) { /* memory copy still works */ }
  }

  function indexOf(handle) {
    for (var i = 0; i < items.length; i++) if (items[i].h === handle) return i;
    return -1;
  }

  function emit(type, handle) {
    document.dispatchEvent(new CustomEvent('ft:wishlist:change', {
      detail: { type: type, handle: handle, items: items.slice() }
    }));
  }

  var api = {
    has: function (handle) { return indexOf(handle) !== -1; },
    count: function () { return items.length; },
    list: function () { return items.slice(); },
    add: function (handle, id, title, variantId) {
      if (!handle || api.has(handle)) return false;
      var entry = { h: handle, id: id ? String(id) : '', t: Date.now() };
      if (variantId) entry.v = String(variantId); // the exact option the shopper had picked on the product page
      items.unshift(entry);
      if (items.length > MAX_ITEMS) items.length = MAX_ITEMS;
      save();
      track('add_to_wishlist', handle, id, title);
      emit('add', handle);
      return true;
    },
    remove: function (handle) {
      var i = indexOf(handle);
      if (i === -1) return null;
      var removed = items.splice(i, 1)[0];
      save();
      emit('remove', handle);
      return removed;
    },
    entry: function (handle) {
      var i = indexOf(handle);
      return i === -1 ? null : items[i];
    },
    setVariant: function (handle, variantId) {
      var i = indexOf(handle);
      if (i === -1) return false;
      items[i].v = String(variantId);
      save();
      emit('update', handle);
      return true;
    },
    restore: function (entry, position) {
      if (!entry || api.has(entry.h)) return;
      items.splice(Math.min(position || 0, items.length), 0, entry);
      save();
      emit('add', entry.h);
    },
    clear: function () {
      items = [];
      save();
      emit('clear');
    }
  };

  // Sent straight to GA4 (like the quiz events). Do NOT also tag this in GTM or it double-counts.
  function track(name, handle, id, title) {
    try {
      if (typeof window.gtag === 'function') {
        if (name === 'share') window.gtag('event', 'share', { method: handle, content_type: 'wishlist' });
        else window.gtag('event', name, {
          items: [{ item_id: id ? String(id) : handle, item_name: title || handle }],
          // flat copy of the product name: GA4 cannot rank an event by item, but it can by an event-scoped custom dimension
          wishlist_item: String(title || handle).slice(0, 100)
        });
      }
    } catch (e) { /* analytics must never break the list */ }
  }

  /* ---------- buttons + header count ---------- */

  // On a product page, a saved item whose picked option has since changed offers to update it.
  function isStale(b, handle) {
    if (!b.classList.contains('ft-wl-btn--pdp')) return false;
    var picker = document.getElementById('all-product-variants');
    var e = api.entry(handle);
    return !!(picker && e && e.v && picker.value && e.v !== picker.value);
  }

  function syncButtons(root) {
    var buttons = (root || document).querySelectorAll('[data-ft-wl]');
    for (var i = 0; i < buttons.length; i++) {
      var b = buttons[i];
      var handle = b.getAttribute('data-handle');
      var on = api.has(handle);
      var stale = on && isStale(b, handle);
      var state = on + ':' + stale;
      if (b.getAttribute('data-state') === state) continue;
      b.setAttribute('data-state', state);
      b.setAttribute('aria-pressed', String(on));
      b.classList.toggle('is-saved', on);
      b.classList.toggle('is-stale', stale);
      var title = b.getAttribute('data-title') || 'this item';
      b.setAttribute('aria-label', stale ? 'Update saved option for ' + title : (on ? 'Remove ' : 'Save ') + title + (on ? ' from wishlist' : ' to wishlist'));
      var label = b.querySelector('.ft-wl-btn__label');
      if (label) label.textContent = stale ? 'Update saved option' : on ? 'Saved to wishlist' : 'Save to wishlist';
    }
  }

  function syncCount() {
    var n = api.count();
    var bubbles = document.querySelectorAll('[data-ft-wl-count]');
    for (var i = 0; i < bubbles.length; i++) {
      bubbles[i].textContent = n > 99 ? '99+' : String(n);
      bubbles[i].classList.toggle('is-empty', n === 0);
    }
  }

  function syncAll() { syncButtons(); syncCount(); }

  /* ---------- toast ---------- */

  var toastEl = null;
  var toastTimer = null;

  function toast(message, actionLabel, actionFn) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'ft-wl-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = '';
    var msg = document.createElement('span');
    msg.textContent = message;
    toastEl.appendChild(msg);
    if (actionLabel) {
      var a = document.createElement(typeof actionFn === 'string' ? 'a' : 'button');
      a.className = 'ft-wl-toast__action';
      a.textContent = actionLabel;
      if (typeof actionFn === 'string') a.href = actionFn;
      else { a.type = 'button'; a.addEventListener('click', function () { actionFn(); hideToast(); }); }
      toastEl.appendChild(a);
    }
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, 4500);
  }

  function hideToast() {
    if (toastEl) toastEl.classList.remove('is-visible');
  }

  function pageUrl() {
    return window.FT_WISHLIST_URL || '/pages/wishlist';
  }

  /* ---------- heart clicks ---------- */

  // Capture phase: the heart sits inside the product card's <a>, so stop the navigation first.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-ft-wl]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var handle = btn.getAttribute('data-handle');
    var onPage = !!document.querySelector('[data-ft-wl-page]');

    // Saving is account-only: there is nowhere to persist a guest's list, so
    // rather than save locally and lose it, send them to sign in and bring them
    // straight back here with the piece still pending.
    if (requiresLogin()) {
      var pdpPicker = btn.classList.contains('ft-wl-btn--pdp') ? document.getElementById('all-product-variants') : null;
      sendToLogin({
        h: handle,
        id: btn.getAttribute('data-product-id') || '',
        t: btn.getAttribute('data-title') || '',
        v: (pdpPicker && pdpPicker.value) || ''
      });
      return;
    }
    if (api.has(handle) && isStale(btn, handle)) {
      api.setVariant(handle, document.getElementById('all-product-variants').value);
      toast('Updated your saved option', 'View wishlist', pageUrl());
    } else if (api.has(handle)) {
      var idx = indexOf(handle);
      var removed = api.remove(handle);
      toast('Removed from your wishlist', 'Undo', function () { api.restore(removed, idx); });
    } else {
      // From the product page, remember the selected variant so the wishlist can add that exact piece.
      var picker = btn.classList.contains('ft-wl-btn--pdp') ? document.getElementById('all-product-variants') : null;
      api.add(handle, btn.getAttribute('data-product-id'), btn.getAttribute('data-title'), picker && picker.value);
      if (onPage) toast('Saved to your wishlist');
      else toast('Saved to your wishlist', 'View wishlist', pageUrl());
    }
  }, true);

  document.addEventListener('ft:wishlist:change', syncAll);

  window.addEventListener('storage', function (e) {
    if (e.key !== KEY) return;
    items = load();
    syncAll();
    emit('external');
  });

  // Collection filters, infinite scroll and quick views add cards after load.
  var syncTimer = null;
  function watchDom() {
    if (!window.MutationObserver) return;
    new MutationObserver(function () {
      clearTimeout(syncTimer);
      syncTimer = setTimeout(function () { syncButtons(); }, 150);
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ---------- add to cart ---------- */

  function whenCartApi(timeoutMs) {
    return new Promise(function (resolve) {
      if (window.CartAPI) return resolve(window.CartAPI);
      // The theme lazy-loads the cart code on the first gesture; a click here is one, but nudge it anyway.
      try { document.dispatchEvent(new Event('mousemove')); } catch (e) { /* ignore */ }
      var waited = 0;
      var t = setInterval(function () {
        waited += 150;
        if (window.CartAPI) { clearInterval(t); resolve(window.CartAPI); }
        else if (waited >= timeoutMs) { clearInterval(t); resolve(null); }
      }, 150);
    });
  }

  // Same endpoint and wording as the product page (snippets/shipping-calculate.liquid).
  var LEAD_URL = 'https://apps.freshtrends.com/API/Stock/estimated_time_delivery_instock_latest.php';
  var LEAD_FALLBACK = 'Made to order';
  var leadCache = {};

  function leadTime(variantId) {
    if (!variantId) return Promise.resolve(LEAD_FALLBACK);
    if (leadCache[variantId]) return leadCache[variantId];
    leadCache[variantId] = fetch(LEAD_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: 'variant_sku=' + encodeURIComponent(variantId)
    }).then(function (r) { return r.json(); }).then(function (data) {
      if (!data || !data.success) return LEAD_FALLBACK;
      if (data.data === 'in_stock') return 'Ships in 4-7 days';
      var maxWeeks = Math.ceil((parseInt(data.ships_in, 10) || 10) / 7);
      return maxWeeks <= 1 ? 'Made to order: Ready in 1 week' : 'Made to order: Ready in 1\u2013' + maxWeeks + ' weeks';
    }).catch(function () {
      delete leadCache[variantId];
      return LEAD_FALLBACK;
    });
    return leadCache[variantId];
  }

  // lines: [{ id: variantId, quantity: 1 }, ...]
  function addLines(lines) {
    var body = JSON.stringify({ items: lines });
    return whenCartApi(4000).then(function (cartApi) {
      if (cartApi && cartApi.addItem && cartApi.openCartDrawer) {
        return cartApi.addItem(body, 'application/json').then(function (res) {
          if (!res) throw new Error('add failed');
          return cartApi.openCartDrawer();
        });
      }
      // Cart drawer code never loaded: add through the AJAX API, then show the cart page.
      return fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: body
      }).then(function (r) {
        if (!r.ok) throw new Error('add failed');
        window.location.href = '/cart';
      });
    });
  }

  function addToCart(variantId) {
    return addLines([{ id: Number(variantId), quantity: 1 }]);
  }

  /* ---------- login gate ---------- */

  // Phase 2 config, published by snippets/ft-wishlist-bootstrap.liquid. Absent
  // on a page that never rendered the bootstrap, so everything here degrades to
  // "no gate" rather than throwing.
  var SYNC = window.FT_WISHLIST_SYNC || {};
  var PENDING_KEY = 'ft_wishlist_pending';

  function requiresLogin() {
    return !!SYNC.requireLogin && !SYNC.loggedIn;
  }

  // Send a signed-out shopper to sign in, remembering both the page they were
  // on and the piece they were trying to save.
  function sendToLogin(entry) {
    try {
      sessionStorage.setItem(PENDING_KEY, JSON.stringify(entry));
    } catch (e) {
      // Private window or storage blocked: they simply click the heart again
      // after signing in. Not worth blocking the redirect over.
    }
    // return_to MUST be relative - Shopify rejects absolute URLs here - and is
    // what brings them back to this exact page instead of the account index.
    var back = window.location.pathname + window.location.search;
    window.location.href = '/customer_authentication/login?return_to=' + encodeURIComponent(back);
  }

  // Runs once on load: if they just came back from signing in, save the piece
  // they originally clicked so the trip is invisible to them.
  function flushPending() {
    if (!SYNC.loggedIn) return;
    var raw = null;
    try {
      raw = sessionStorage.getItem(PENDING_KEY);
      sessionStorage.removeItem(PENDING_KEY);
    } catch (e) { return; }
    if (!raw) return;
    try {
      var p = JSON.parse(raw);
      if (p && p.h && !api.has(p.h)) {
        api.add(p.h, p.id, p.t, p.v);
        toast('Saved to your wishlist', 'View wishlist', pageUrl());
      }
    } catch (e) { /* malformed: drop it silently */ }
  }

  /* ---------- sharing ---------- */

  // A shared list travels in the link itself: ?list=handle:variantId,handle,...&from=Name (no server involved).
  var SHARE_MAX = 40;
  var NAME_KEY = 'ft_wishlist_name';

  function parseShared() {
    var m = /[?&]list=([^&]*)/.exec(window.location.search);
    if (!m) return null;
    var items = [];
    try {
      decodeURIComponent(m[1]).split(',').forEach(function (part) {
        var bits = part.split(':');
        var h = (bits[0] || '').toLowerCase();
        if (!/^[a-z0-9][a-z0-9-]*$/.test(h) || items.length >= SHARE_MAX) return;
        var e = { h: h };
        if (/^\d{5,}$/.test(bits[1] || '')) e.v = bits[1];
        items.push(e);
      });
    } catch (e) { /* malformed link: treat as empty */ }
    var from = '';
    var f = /[?&]from=([^&]*)/.exec(window.location.search);
    if (f) { try { from = decodeURIComponent(f[1].replace(/\+/g, ' ')).slice(0, 40).trim(); } catch (e) { from = ''; } }
    if (/[@<>]/.test(from)) from = '';
    return { items: items, from: from };
  }

  // `items` lets the product page share the piece being viewed when the
  // wishlist is empty; everywhere else it is the shopper's own list.
  function shareLink(name, items) {
    var parts = (items || api.list()).slice(0, SHARE_MAX).map(function (it) { return it.v ? it.h + ':' + it.v : it.h; });
    var base = (window.FT_WISHLIST_URL || window.location.pathname);
    var url = window.location.origin + base + '?list=' + parts.join(',');
    if (name) url += '&from=' + encodeURIComponent(name);
    return url;
  }

  function shareWords(name) {
    return (name ? name + ' is sharing' : 'Sharing') + ' a FreshTrends wishlist with you';
  }

  /* ---------- share dialog (wishlist page AND product page) ---------- */

  // One dialog per page, driven by every [data-ft-wl-share-open] on it. Lives
  // here rather than in initPage because the product page has no wishlist
  // page to hang it off.
  function initShare() {
    var dlg = document.querySelector('[data-ft-wl-sharedlg]');
    if (!dlg) return;

    var q = function (sel) { return dlg.querySelector(sel); };
    var nameInput = q('[data-ft-wl-share-name]');
    var toInput = q('[data-ft-wl-share-to]');
    var noteInput = q('[data-ft-wl-share-note]');
    var msg = q('[data-ft-wl-share-msg]');
    var scopeEl = q('[data-ft-wl-share-scope]');
    var sendBtn = q('[data-ft-wl-share-send]');
    var emailBox = q('.ft-wl-sharedlg__email');
    var orRule = q('.ft-wl-sharedlg__or');
    var sentBox = q('[data-ft-wl-share-sent]');
    var sentTo = q('[data-ft-wl-share-sentto]');

    var items = null;   // what this opening shares; null = the shopper's list

    function say(text, isError) {
      msg.textContent = text || '';
      msg.classList.toggle('is-error', !!isError);
    }

    // The email form and the confirmation occupy the same slot: exactly one of
    // them is visible. The link row above is left alone either way.
    function showForm() {
      if (emailBox) emailBox.hidden = false;
      if (orRule) orRule.hidden = false;
      if (sentBox) sentBox.hidden = true;
    }

    function showSent(address) {
      if (sentTo) sentTo.textContent = address;
      if (emailBox) emailBox.hidden = true;
      if (orRule) orRule.hidden = true;
      if (sentBox) sentBox.hidden = false;
    }

    function shareName() {
      var name = nameInput ? nameInput.value.trim().slice(0, 40) : '';
      // A browser can autofill the shopper's email here: never share that.
      return /[@<>]/.test(name) ? '' : name;
    }

    function current() {
      var list = items || api.list();
      var name = shareName();
      try { localStorage.setItem(NAME_KEY, name); } catch (e) { /* optional */ }
      return { list: list, name: name, url: shareLink(name, list), words: shareWords(name) };
    }

    // On a product page with an empty wishlist there is nothing to share, so
    // the opener's own product stands in.
    function scopeFor(opener) {
      var handle = opener.getAttribute('data-ft-wl-share-handle');
      if (!handle || api.count() > 0) return null;
      var entry = { h: handle };
      var picker = document.getElementById('all-product-variants');
      if (picker && /^\d{5,}$/.test(picker.value || '')) entry.v = picker.value;
      return [entry];
    }

    function open(opener) {
      items = scopeFor(opener);
      var count = (items || api.list()).length;

      if (items) {
        scopeEl.textContent = 'Your wishlist is empty, so this shares the piece you are looking at.';
      } else if (count > SHARE_MAX) {
        scopeEl.textContent = 'Your list is long: the ' + SHARE_MAX + ' most recent pieces will be shared.';
      } else {
        scopeEl.textContent = count === 1 ? '1 piece' : count + ' pieces';
      }

      if (nameInput && !nameInput.value) {
        try {
          var saved = localStorage.getItem(NAME_KEY) || '';
          nameInput.value = /[@<>]/.test(saved) ? '' : saved;
        } catch (e) { /* optional */ }
      }
      q('[data-ft-wl-share-native]').hidden = !navigator.share;
      q('[data-ft-wl-share-sms]').hidden = !(window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
      say('');
      showForm();
      if (toInput) toInput.classList.remove('is-missing');

      if (dlg.showModal) dlg.showModal();
      else dlg.setAttribute('open', '');   // no top layer, but still usable
      if (nameInput) nameInput.focus();
    }

    function close() {
      if (dlg.close) dlg.close();
      else dlg.removeAttribute('open');
    }

    function copyLink() {
      var info = current();
      function done() { say('Link copied. Paste it into a message or email.'); track('share', 'wishlist-copy'); }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(info.url).then(done, function () { window.prompt('Copy this link:', info.url); });
      } else {
        window.prompt('Copy this link:', info.url);
      }
    }

    function nativeShare() {
      var info = current();
      if (!navigator.share) return;
      // text carries the url too: some share targets (e.g. Gmail on Windows)
      // treat a separate url as a bare URI and drop text entirely.
      navigator.share({ title: info.words, text: info.words + '\n\n' + info.url }).then(function () {
        track('share', 'wishlist-native');
      }, function () { /* canceled */ });
    }

    function sendEmail() {
      var info = current();
      var to = toInput ? toInput.value.trim() : '';
      // Deliberately loose: the real check is Klaviyo's, and a regex that tries
      // to be clever here rejects valid addresses.
      if (!to || !/^[^@\s]+@[^@\s.]+\.[^@\s]{2,}$/.test(to)) {
        if (toInput) { toInput.classList.add('is-missing'); toInput.focus(); }
        say('Please enter their email address.', true);
        return;
      }
      if (!info.list.length) { say('There is nothing to share yet.', true); return; }

      toInput.classList.remove('is-missing');
      var label = sendBtn.querySelector('span').textContent;
      sendBtn.disabled = true;
      sendBtn.querySelector('span').textContent = 'Sending…';
      say('');

      fetch(proxyBase() + '/share', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: to,
          from_name: info.name,
          note: noteInput ? noteInput.value.trim().slice(0, 300) : '',
          url: info.url,
          items: info.list.slice(0, SHARE_MAX)
        })
      }).then(function (res) {
        if (!res.ok) throw new Error(res.status);
        return res.json();
      }).then(function () {
        // aria-live on the status line is what announces this to a screen
        // reader; the panel itself is the visual confirmation.
        say('Sent to ' + to + '.');
        showSent(to);
        track('share', 'wishlist-email');
        toInput.value = '';
        if (noteInput) noteInput.value = '';
      }).catch(function () {
        // Never strand the shopper: the link still works without us.
        say('We could not send that just now. Try "Copy link" instead.', true);
      }).then(function () {
        sendBtn.disabled = false;
        sendBtn.querySelector('span').textContent = label;
      });
    }

    function proxyBase() {
      var cfg = window.FT_WISHLIST_SYNC;
      return (cfg && cfg.proxyBase) || '/apps/wishlist';
    }

    function refreshLinks() {
      var info = current();
      var sms = q('[data-ft-wl-share-sms]');
      var mailto = q('[data-ft-wl-share-mailto]');
      if (sms) sms.href = 'sms:?&body=' + encodeURIComponent(info.words + ': ' + info.url);
      if (mailto) mailto.href = 'mailto:?subject=' + encodeURIComponent(info.words) + '&body=' + encodeURIComponent(info.words + ':\n\n' + info.url);
    }

    // Delegated, not bound per button: a product page can render its Add to
    // Cart block more than once, and some of those arrive after this runs.
    document.addEventListener('click', function (e) {
      var opener = e.target.closest && e.target.closest('[data-ft-wl-share-open]');
      if (!opener) return;
      e.preventDefault();
      open(opener);
      refreshLinks();
    });

    dlg.addEventListener('click', function (e) {
      var t = e.target;
      // Every control in here wraps an SVG, and the event target can be the
      // svg/path rather than the button.
      if (!t || !t.closest) return;
      if (t.closest('[data-ft-wl-share-copy]')) { copyLink(); return; }
      if (t.closest('[data-ft-wl-share-native]')) { nativeShare(); return; }
      if (t.closest('[data-ft-wl-share-send]')) { sendEmail(); return; }
      if (t.closest('[data-ft-wl-share-again]')) {
        showForm();
        say('');
        if (toInput) toInput.focus();
        return;
      }
      if (t.closest('[data-ft-wl-share-sms]') || t.closest('[data-ft-wl-share-mailto]')) {
        refreshLinks();
        track('share', 'wishlist-link');
        close();
      }
    });

    dlg.addEventListener('input', function (e) {
      if (e.target.closest && e.target.closest('[data-ft-wl-share-name]')) refreshLinks();
    });

    // Clicking the backdrop closes it. <dialog> reports backdrop clicks as
    // clicks on the dialog itself, so compare against its box.
    dlg.addEventListener('mousedown', function (e) {
      if (e.target !== dlg) return;
      var r = dlg.getBoundingClientRect();
      var inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      if (!inside) close();
    });
  }

  /* ---------- wishlist page ---------- */

  // A signed-out visitor has no list to show, so the page asks them to sign in
  // rather than claiming their wishlist is empty - which would read as "we lost
  // your saves".
  function showSignedOutPage(root) {
    var toolbar = root.querySelector('[data-ft-wl-toolbar]');
    var summary = root.querySelector('[data-ft-wl-summary]');
    var emptyBox = root.querySelector('[data-ft-wl-empty]');
    if (toolbar) toolbar.hidden = true;
    if (summary) summary.textContent = '';
    if (!emptyBox) return;
    var title = emptyBox.querySelector('.ft-wl-empty__title');
    var text = emptyBox.querySelector('.ft-wl-empty__text');
    var cta = emptyBox.querySelector('a');
    if (title) title.textContent = 'Sign in to see your wishlist';
    if (text) text.textContent = 'Your saved pieces are kept with your account, so they are there on every device.';
    if (cta) {
      cta.textContent = 'Sign in';
      cta.href = '/customer_authentication/login?return_to=' + encodeURIComponent(window.location.pathname);
    }
    emptyBox.hidden = false;
  }

  function initPage(root) {
    // A shared ?list= link is public by design - the recipient is usually
    // someone without an account, and the whole point is that they can open it.
    // So the sign-in gate applies only to a visitor looking at THEIR OWN list.
    if (requiresLogin() && !parseShared()) { showSignedOutPage(root); return; }
    var grid = root.querySelector('[data-ft-wl-grid]');
    var emptyBox = root.querySelector('[data-ft-wl-empty]');
    var toolbar = root.querySelector('[data-ft-wl-toolbar]');
    var summary = root.querySelector('[data-ft-wl-summary]');
    var cache = {};      // handle -> element (or null when the product no longer exists)
    var pending = {};    // handle -> true while fetching

    // Opened from a shared link? Then show that list (read-only) instead of the visitor's own.
    var shared = parseShared();
    var sharedList = shared ? shared.items : null;

    function listNow() { return shared ? sharedList : api.list(); }

    function inList(handle) {
      var l = listNow();
      for (var i = 0; i < l.length; i++) if (l[i].h === handle) return true;
      return false;
    }

    function setupMode() {
      if (!shared) return;
      root.classList.add('is-shared');
      var meta = document.createElement('meta');
      meta.name = 'robots';
      meta.content = 'noindex,nofollow';
      document.head.appendChild(meta);
      var title = root.querySelector('[data-ft-wl-title]');
      if (title) title.textContent = shared.from ? shared.from + '\u2019s Wishlist' : 'A Shared Wishlist';
      var note = root.querySelector('[data-ft-wl-note]');
      if (note) note.textContent = 'Choose options and add pieces to your cart, or save them to your own wishlist.';
      var hide = ['[data-ft-wl-share-open]', '[data-ft-wl-clear]'];
      hide.forEach(function (sel) { var e = root.querySelector(sel); if (e) e.hidden = true; });
      ['[data-ft-wl-saveall]', '[data-ft-wl-mine]'].forEach(function (sel) { var e = root.querySelector(sel); if (e) e.hidden = false; });
      var mine = root.querySelector('[data-ft-wl-mine]');
      if (mine) mine.href = window.location.pathname;
    }

    function saveAllShared() {
      // Saving someone else's shared list into YOUR wishlist is still a save,
      // so it needs the same gate the hearts have. No pending entry: this is a
      // whole list, and they can press it again once signed in.
      if (requiresLogin()) {
        sendToLogin({ h: '', id: '', t: '', v: '' });
        return;
      }
      var added = 0;
      sharedList.forEach(function (it) {
        if (cache[it.h] === null) return;
        if (api.add(it.h, '', it.h, it.v)) added++;
      });
      var msg = root.querySelector('[data-ft-wl-addall-msg]');
      msg.textContent = added ? 'Saved ' + added + (added === 1 ? ' piece' : ' pieces') + ' to your wishlist. ' : 'Those pieces are already on your wishlist. ';
      var a = document.createElement('a');
      a.href = window.location.pathname;
      a.textContent = 'View my wishlist';
      msg.appendChild(a);
    }

    function fetchItem(handle) {
      pending[handle] = true;
      return fetch('/products/' + encodeURIComponent(handle) + '?section_id=ft-wishlist-item', { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
        .then(function (html) {
          var tpl = document.createElement('template');
          tpl.innerHTML = html;
          var el = tpl.content.querySelector('.ft-wl-item');
          cache[handle] = el || null;
        })
        .catch(function (err) {
          // 404 = unpublished or deleted product: drop it quietly. Other errors: retry on next render.
          if (String(err && err.message) === '404') cache[handle] = null;
        })
        .then(function () { delete pending[handle]; });
    }

    function render() {
      var list = listNow();
      var waiting = [];
      list.forEach(function (it) {
        if (!(it.h in cache) && !pending[it.h]) waiting.push(fetchItem(it.h));
      });
      if (waiting.length) {
        root.classList.add('is-loading');
        Promise.all(waiting).then(function () {
          // Purge products that no longer exist, then paint.
          list.forEach(function (it) {
            if (cache[it.h] !== null) return;
            if (shared) sharedList = sharedList.filter(function (x) { return x.h !== it.h; });
            else api.remove(it.h);
          });
          root.classList.remove('is-loading');
          render();
        });
      }

      var present = list.filter(function (it) { return cache[it.h]; });
      // Remove cards that left the list; add/reorder the rest.
      Array.prototype.slice.call(grid.children).forEach(function (child) {
        if (!inList(child.getAttribute('data-handle'))) grid.removeChild(child);
      });
      present.forEach(function (it, i) {
        var el = cache[it.h];
        if (grid.children[i] !== el) grid.insertBefore(el, grid.children[i] || null);
        buildOptions(el, it.v);
      });

      var total = list.length;
      root.classList.toggle('is-empty', total === 0);
      if (emptyBox) emptyBox.hidden = total !== 0;
      if (toolbar) toolbar.hidden = total === 0;
      if (summary) {
        summary.textContent = total === 0 ? '' : shared
          ? (total === 1 ? '1 piece shared with you' : total + ' pieces shared with you')
          : (total === 1 ? '1 saved piece' : total + ' saved pieces');
      }
      if (shared && total === 0 && emptyBox) {
        var et = emptyBox.querySelector('.ft-wl-empty__title');
        var tx = emptyBox.querySelector('.ft-wl-empty__text');
        if (et) et.textContent = 'This wishlist is empty';
        if (tx) tx.textContent = 'The pieces in this link may no longer be available.';
      }

      syncButtons(root);
    }

    // Option pickers on the card (Metal, Style, Gauge...). Nothing is pre-chosen unless the shopper hearted
    // from the product page with options picked: sizes are made to order, so a silent default could be wrong.
    function buildOptions(el, savedId) {
      var box = el.querySelector('[data-ft-wl-options]');
      if (!box || box.getAttribute('data-built')) return;
      var data = {};
      try { data = JSON.parse(el.querySelector('[data-ft-wl-data]').textContent); } catch (e) { return; }
      var variants = data.variants || [];
      var options = (data.options || []).filter(function (o) { return o.name !== 'Title'; });
      if (!variants.length || !options.length) return;
      box.setAttribute('data-built', '1');
      box.textContent = '';

      var saved = variants.filter(function (v) { return String(v.id) === String(savedId); })[0];
      var selects = options.map(function (o, i) {
        var wrap = document.createElement('label');
        wrap.className = 'ft-wl-item__field';
        var name = document.createElement('span');
        name.textContent = o.name;
        var sel = document.createElement('select');
        sel.className = 'ft-wl-item__select';
        sel.setAttribute('data-ft-wl-option', o.name);
        var blank = document.createElement('option');
        blank.value = '';
        blank.textContent = 'Select ' + o.name;
        sel.appendChild(blank);
        o.values.forEach(function (val) {
          var opt = document.createElement('option');
          opt.value = val;
          opt.textContent = val;
          sel.appendChild(opt);
        });
        if (saved && saved.o[i]) sel.value = saved.o[i];
        wrap.appendChild(name);
        wrap.appendChild(sel);
        box.appendChild(wrap);
        return sel;
      });

      var note = document.createElement('p');
      note.className = 'ft-wl-item__note';
      note.setAttribute('data-ft-wl-note', '');
      box.appendChild(note);

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ft-wl-primary';
      btn.setAttribute('data-ft-wl-atc', '');
      btn.setAttribute('data-variant-id', '');
      btn.textContent = 'Add to cart';
      box.appendChild(btn);

      var hasSale = !!el.querySelector('.price-strike'); // the card's own sale math is not repeated here
      var basePrice = Math.min.apply(null, variants.map(function (v) { return v.price; }));

      // Gray out (disable) every choice that no in-stock variant offers together with the other picks.
      function refresh() {
        selects.forEach(function (sel, i) {
          Array.prototype.forEach.call(sel.options, function (opt) {
            if (!opt.value) return;
            var exists = false;
            var inStock = false;
            variants.forEach(function (v) {
              if (v.o[i] !== opt.value) return;
              var fits = selects.every(function (other, j) { return j === i || !other.value || v.o[j] === other.value; });
              if (!fits) return;
              exists = true;
              if (v.available) inStock = true;
            });
            opt.disabled = !inStock;
            opt.textContent = opt.value + (exists && !inStock ? ' (sold out)' : '');
          });
        });
      }

      function settle() {
        // A pick made earlier can become impossible after another choice: clear it, then re-gray.
        refresh();
        var cleared = false;
        selects.forEach(function (sel) {
          if (sel.value && sel.options[sel.selectedIndex].disabled) { sel.value = ''; cleared = true; }
        });
        if (cleared) refresh();
      }

      // The card photo follows the picks: first variant that fits everything chosen so far and has its own image.
      var photo = el.querySelector('.product-card__grid-item-image-wrapper .grid__item_image img');
      var photoOriginal = photo ? { src: photo.getAttribute('src'), srcset: photo.getAttribute('srcset') } : null;

      function showPhoto(vals) {
        if (!photo) return;
        var chosen = vals.some(function (v) { return v; });
        var fit = chosen ? variants.filter(function (v) {
          return v.img && vals.every(function (val, i) { return !val || v.o[i] === val; });
        })[0] : null;
        if (fit) {
          if (photo.getAttribute('src') !== fit.img) {
            photo.setAttribute('src', fit.img);
            photo.setAttribute('srcset', fit.img + ' 600w');
          }
        } else if (!chosen && photoOriginal && photo.getAttribute('src') !== photoOriginal.src) {
          photo.setAttribute('src', photoOriginal.src);
          if (photoOriginal.srcset) photo.setAttribute('srcset', photoOriginal.srcset);
        }
      }

      function update() {
        var vals = selects.map(function (s) { return s.value; });
        showPhoto(vals);
        selects.forEach(function (s) { s.classList.remove('is-missing'); });
        btn.setAttribute('data-variant-id', '');
        note.textContent = '';
        if (vals.some(function (v) { return !v; })) return;
        var match = variants.filter(function (v) {
          return vals.every(function (val, i) { return v.o[i] === val; });
        })[0];
        if (!match) { note.textContent = 'That combination is not available.'; return; }
        if (!match.available) { note.textContent = 'Sold out in this option.'; return; }
        btn.setAttribute('data-variant-id', String(match.id));
        if (!hasSale && match.price !== basePrice && window.Shopify && Shopify.formatMoney) {
          note.textContent = Shopify.formatMoney(match.price).replace(/\.00($|[^0-9])/, '$1');
        }
      }
      selects.forEach(function (s) { s.addEventListener('change', function () { settle(); update(); }); });
      settle();
      update();
    }

    function addAll() {
      var msg = root.querySelector('[data-ft-wl-addall-msg]');
      var ready = [];
      var waiting = [];
      Array.prototype.forEach.call(grid.querySelectorAll('.ft-wl-item'), function (item) {
        var btn = item.querySelector('[data-ft-wl-atc]');
        if (!btn) return; // sold out: nothing to add
        var id = btn.getAttribute('data-variant-id');
        if (id) ready.push({ id: Number(id), quantity: 1 });
        else waiting.push(item);
      });
      waiting.forEach(function (item) {
        Array.prototype.forEach.call(item.querySelectorAll('[data-ft-wl-option]'), function (sel) {
          if (!sel.value) sel.classList.add('is-missing');
        });
      });
      var waitText = waiting.length === 1 ? '1 piece still needs its options' : waiting.length + ' pieces still need their options';
      if (!ready.length) {
        msg.textContent = waiting.length ? 'Choose options first: ' + waitText + ' (marked in red below).' : 'Nothing here can be added right now.';
        if (waiting.length) waiting[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      msg.textContent = 'Adding…';
      addLines(ready).then(function () {
        msg.textContent = 'Added ' + (ready.length === 1 ? '1 piece' : ready.length + ' pieces') + ' to your cart.' +
          (waiting.length ? ' ' + waitText.charAt(0).toUpperCase() + waitText.slice(1) + ' (marked in red below).' : '');
        if (waiting.length) waiting[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }).catch(function () {
        msg.textContent = 'Something went wrong adding to your cart. Please try again.';
      });
    }

    /* events */
    root.addEventListener('click', function (e) {
      var t = e.target;
      if (!t.closest) return;

      if (t.closest('[data-ft-wl-saveall]')) { saveAllShared(); return; }

      if (t.closest('[data-ft-wl-addall]')) { addAll(); return; }

      if (t.closest('[data-ft-wl-clear]')) {
        if (window.confirm('Remove everything from your wishlist?')) api.clear();
        return;
      }

      var atc = t.closest('[data-ft-wl-atc]');
      if (atc) {
        var item = atc.closest('.ft-wl-item');
        var variantId = atc.getAttribute('data-variant-id');
        var msg = item.querySelector('[data-ft-wl-msg]');
        var label = atc.textContent;
        if (!variantId) {
          var missing = Array.prototype.filter.call(item.querySelectorAll('[data-ft-wl-option]'), function (x) { return !x.value; })[0];
          if (missing) {
            missing.classList.add('is-missing');
            missing.focus();
            if (msg) msg.textContent = 'Please choose ' + missing.getAttribute('data-ft-wl-option') + '.';
          }
          return;
        }
        atc.disabled = true;
        atc.textContent = 'Adding…';
        if (msg) msg.textContent = '';
        addToCart(variantId).then(function () {
          atc.textContent = 'Added ✓';
          setTimeout(function () { atc.textContent = label; atc.disabled = false; }, 1800);
        }).catch(function () {
          atc.textContent = label;
          atc.disabled = false;
          if (msg) msg.textContent = 'That option could not be added. Please try again or open the product page.';
        });
      }
    });

    document.addEventListener('ft:wishlist:change', render);
    setupMode();
    render();
  }

  // The product page's option picker is changed by jQuery .trigger(), which native listeners never hear.
  function watchPicker() {
    var picker = document.getElementById('all-product-variants');
    if (!picker || !document.querySelector('.ft-wl-btn--pdp')) return;
    var last = picker.value;
    setInterval(function () {
      if (picker.value !== last) { last = picker.value; syncButtons(); }
    }, 500);
  }

  function boot() {
    flushPending();
    syncAll();
    watchDom();
    watchPicker();
    var page = document.querySelector('[data-ft-wl-page]');
    if (page) initPage(page);
    initShare();
  }

  window.FTWishlist = api;

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
